import { PropsWithChildren } from "react";
import { FC } from "react";
import { Platform } from "react-native";
import {
  FlatList,
  FlatListProps,
  StyleProp,
  StyleSheet,
  Text,
  TextProps,
  TouchableOpacity,
  View,
  ViewProps,
  ViewStyle,
} from "react-native";
import { tHeaderInstance } from "src/hooks/table";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { PdStack } from ".";
import { PdIconButton } from "../forms/icon-button";
import { PdArrowLeft, PdArrowRight, PdChevronDown, PdChevronUp } from "../Icons";

import { PdText, tPdText } from "./typography";

export const PdTabLabel: FC<{ isActive?: boolean } & TextProps> = ({ isActive, style, ...rest }) => (
  <PdText style={[isActive ? styles.pdTabLabelActive : {}, style]} {...rest} />
);

export const PdTableHeader: FC<tPdText> = ({ style, ...rest }) => (
  <Text style={[styles.pdTableHeaderLabel, style]} {...rest} />
);

export const PdTableRow: FC<ViewProps & { isHovered?: boolean; isPressed?: boolean }> = ({
  isHovered,
  isPressed,
  style,
  ...rest
}) => (
  <View
    style={[
      styles.pdTableRow,
      isHovered ? styles.pdTableRowHovered : {},
      isPressed ? styles.pdTableRowPressed : {},
      style,
    ]}
    {...rest}
  />
);

export const PdTableCell: FC<ViewProps> = ({ style, ...rest }) => (
  <View style={[styles.pdTableCell, style]} {...rest} />
);

export const PdTableCellText: FC<tPdText> = ({ style, ...rest }) => (
  <PdText numberOfLines={1} style={style} {...rest} />
);

export const PdTableEmpty: FC<{ message?: string }> = ({ message = "There is nothing to display" }) => {
  return (
    <View style={styles.pdTableEmpty}>
      <PdText style={styles.pdTableEmptyLabel}>{message}</PdText>
    </View>
  );
};

export const PdTable = <T extends unknown>(props: PropsWithChildren<FlatListProps<T>>): React.ReactElement | null => {
  const { style, contentContainerStyle, ...rest } = props;

  return (
    <FlatList
      style={[
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        Platform.OS === "web" ? (webStyles.overflowAuto as StyleProp<ViewStyle>) : {},
        style,
      ]}
      contentContainerStyle={[Platform.OS === "web" ? styles.mrAuto : {}, contentContainerStyle]}
      {...rest}
    />
  );
};

export const PdTableSortableHeaders = <T extends unknown>({
  headers,
}: {
  headers: tHeaderInstance<T>[];
}): JSX.Element => {
  return (
    <View style={styles.pdTableHeaders}>
      {headers.map((header) => (
        <View key={header.id} style={[styles.pdTableRow, header.containerStyle]}>
          <TouchableOpacity
            style={[styles.pdTableHeaderCell, header.style]}
            disabled={header.isSortable === false}
            onPress={header.sort}
          >
            {typeof header.header === "string" ? (
              <PdTableHeader>{header.header}</PdTableHeader>
            ) : (
              header.header({ style: header.style })
            )}
            {!(header.isSortable === false) && (
              <View style={styles.pdTableHeaderIcon}>
                {header.isSorted && header.sortDirection === "asc" ? (
                  <PdChevronUp style={styles.pdTableHeaderChevron} />
                ) : (
                  <PdChevronDown style={styles.pdTableHeaderChevron} />
                )}
              </View>
            )}
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
};

export const PdTablePaginationFooter: React.FC<{
  page: number;
  totalPages?: number;
  hasNext?: boolean;
  onPressBack: () => void;
  onPressNext: () => void;

  style?: StyleProp<ViewStyle>;
}> = ({ page, hasNext, onPressBack, onPressNext, style }) => {
  return (
    <PdStack direction="horizontal" style={[styles.alignCenter, styles.mlAuto, style]}>
      <PdText>{`Page ${page}`}</PdText>
      <PdIconButton
        onPress={onPressBack}
        isDisabled={page === 1}
        variant="ghost"
        icon={<PdArrowLeft style={styles.pdTablePaginationFooterIcon} />}
      />
      <PdIconButton
        onPress={onPressNext}
        isDisabled={!hasNext}
        variant="ghost"
        icon={<PdArrowRight style={styles.pdTablePaginationFooterIcon} />}
      />
    </PdStack>
  );
};

export const PdDivider: FC<ViewProps & { isVertical?: boolean }> = ({ style, isVertical, ...rest }) => (
  <View style={[isVertical ? styles.pdDividerVertical : styles.pdDividerHorizontal, style]} {...rest} />
);

const styles = StyleSheet.create({
  alignCenter: {
    alignItems: "center",
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mrAuto: {
    marginRight: "auto",
  },
  pdDividerHorizontal: {
    backgroundColor: kColors.gray300,
    height: StyleSheet.hairlineWidth,
  },
  pdDividerVertical: {
    backgroundColor: kColors.gray300,
    height: "100%",
    width: StyleSheet.hairlineWidth,
  },
  pdTabLabelActive: {
    fontWeight: "600",
  },
  pdTableCell: {
    alignItems: "center",
    flexDirection: "row",
    height: 40,
    paddingRight: kStyleConst.kSpacing * 4,
  },
  pdTableEmpty: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    paddingVertical: kStyleConst.kSpacing * 2,
  },
  pdTableEmptyLabel: {
    fontStyle: "italic",
  },
  pdTableHeaderCell: {
    alignItems: "flex-end",
    flexDirection: "row",
    flex: 1,
    paddingBottom: kStyleConst.kSpacing,
  },
  pdTableHeaderChevron: {
    color: kColors.gray500,
  },
  pdTableHeaderIcon: {
    alignItems: "center",
    height: 8,
    justifyContent: "center",
    marginBottom: 2,
    width: 8,
  },
  pdTableHeaderLabel: {
    color: kColors.gray500,
    fontFamily: "Raleway",
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.8,
    marginRight: kStyleConst.kSpacing,
    textTransform: "uppercase",
  },
  pdTableHeaders: {
    alignItems: "flex-end",
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderColor: kColors.gray300,
    flexDirection: "row",
    marginBottom: kStyleConst.kSpacing * 2,
    minWidth: "100%",
  },
  pdTablePaginationFooterIcon: {
    width: 20,
  },
  pdTableRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
  },
  pdTableRowHovered: {
    backgroundColor: kColors.gray50,
  },
  pdTableRowPressed: {
    opacity: 0.6,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  overflowAuto: {
    overflow: "auto",
  },
} as const;
